import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '../api/axios';
import { filterPhone, isValidEmail } from '../utils/inputValidation';
import {
  LayoutDashboard, Store, Users, Euro, LogOut, MapPin,
  Loader2, Plus, X, Menu, TrendingUp, ChevronRight, Lock, ClipboardList,
  CheckCircle, XCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import UsersManager from '../components/UsersManager';
import { useToast } from '../components/Toast';

const CA_PIN = '2511';

const NAV_ITEMS = [
  { id: 'overview',      icon: <LayoutDashboard size={20} />, label: 'Vue d\'ensemble'     },
  { id: 'salons',        icon: <Store size={20} />,           label: 'Salons'               },
  { id: 'users',         icon: <Users size={20} />,           label: 'Utilisateurs'         },
  { id: 'inscriptions',  icon: <ClipboardList size={20} />,   label: 'Inscriptions'         },
];

const SuperAdminDashboard = () => {
  const showToast = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [salons, setSalons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [caVisible, setCaVisible]       = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput]         = useState('');
  const [pinError, setPinError]         = useState(false);
  const [pinAttempts, setPinAttempts]   = useState(0);
  const [pinLocked, setPinLocked]       = useState(false);
  const [pendingRegs, setPendingRegs]   = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const pinRef      = useRef(null);
  const logoClicks  = useRef(0);
  const logoTimer   = useRef(null);
  const [formData, setFormData] = useState({
    nomSalon: '', adresse: '', devise: 'XOF',
    nomAdmin: '', prenomAdmin: '', emailAdmin: '',
    passwordAdmin: '', telephoneAdmin: ''
  });

  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, salonsRes] = await Promise.all([
        api.get('/super-admin/stats', { headers }),
        api.get('/super-admin/salons', { headers, params: { limit: 200 } })
      ]);
      setStats(statsRes.data.data);
      setSalons(salonsRes.data.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/');
      else showToast("Erreur de chargement des données", "error");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userName');
    navigate('/');
  };

  const fetchPendingRegistrations = async () => {
    setPendingLoading(true);
    try {
      const res = await api.get('/super-admin/pending-registrations');
      setPendingRegs(res.data.data);
    } catch {
      showToast('Erreur chargement inscriptions', 'error');
    } finally {
      setPendingLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Valider cette inscription et créer le salon ?')) return;
    try {
      await api.post(`/super-admin/pending-registrations/${id}/approve`);
      showToast('Salon activé et identifiants envoyés !', 'success');
      fetchPendingRegistrations();
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur lors de la validation', 'error');
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Rejeter cette demande ?')) return;
    try {
      await api.post(`/super-admin/pending-registrations/${id}/reject`);
      showToast('Demande rejetée', 'success');
      fetchPendingRegistrations();
    } catch {
      showToast('Erreur', 'error');
    }
  };

  const handleResend = async (id) => {
    try {
      await api.post(`/super-admin/pending-registrations/${id}/resend`);
      showToast('Email de vérification renvoyé.', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur', 'error');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setIsMobileMenuOpen(false);
    if (tab === 'inscriptions') fetchPendingRegistrations();
  };

  const handleCreateSalon = async (e) => {
    e.preventDefault();
    if (!isValidEmail(formData.emailAdmin)) {
      showToast('Adresse email invalide', 'error'); return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await api.post('/super-admin/salons', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowModal(false);
      setFormData({
        nomSalon: '', adresse: '', devise: 'XOF',
        nomAdmin: '', prenomAdmin: '', emailAdmin: '',
        passwordAdmin: '', telephoneAdmin: ''
      });
      showToast("Salon créé avec succès !");
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.message || "Erreur lors de la création", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleSalon = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await api.put(`/super-admin/salons/toggle/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      showToast("Erreur lors du changement de statut", "error");
    }
  };

  const tabLabel = NAV_ITEMS.find(n => n.id === activeTab)?.label || '';

  const handleLogoClick = useCallback(() => {
    if (caVisible) { setCaVisible(false); return; }
    logoClicks.current += 1;
    clearTimeout(logoTimer.current);
    logoTimer.current = setTimeout(() => { logoClicks.current = 0; }, 600);
    if (logoClicks.current >= 3) {
      logoClicks.current = 0;
      if (!pinLocked) {
        setPinInput('');
        setPinError(false);
        setShowPinModal(true);
        setTimeout(() => pinRef.current?.focus(), 50);
      }
    }
  }, [caVisible, pinLocked]);

  const submitPin = () => {
    if (pinInput === CA_PIN) {
      setCaVisible(true);
      setShowPinModal(false);
      setPinInput('');
      setPinError(false);
      setPinAttempts(0);
    } else {
      const attempts = pinAttempts + 1;
      setPinAttempts(attempts);
      setPinError(true);
      setPinInput('');
      if (attempts >= 3) {
        setPinLocked(true);
        setShowPinModal(false);
        setTimeout(() => { setPinLocked(false); setPinAttempts(0); }, 30000);
      }
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 font-sans">

      {/* ── OVERLAY MOBILE ── */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* ── SIDEBAR desktop + DRAWER mobile ── */}
      <aside className={`
        fixed h-full z-50 bg-slate-900 text-white flex flex-col
        transition-all duration-300 ease-in-out shadow-2xl
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        w-64
      `}>
        {/* Logo — triple-clic pour accéder aux données confidentielles */}
        <div className="p-6 border-b border-slate-800 cursor-default select-none" onClick={handleLogoClick}>
          <h2 className="text-xl font-bold tracking-tight">
            Sama<span className="text-blue-400">Salon</span>
          </h2>
          <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-1">
            Super Admin
          </p>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1">
          {NAV_ITEMS.map(item => (
            <button
              key={item.id}
              onClick={() => handleTabChange(item.id)}
              className={`flex items-center gap-3 w-full p-3.5 rounded-xl transition-all text-sm font-medium ${
                activeTab === item.id
                  ? 'bg-blue-600 shadow-lg shadow-blue-900/20 text-white'
                  : 'hover:bg-slate-800 text-slate-400'
              }`}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        {/* Déconnexion */}
        <div className="p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full p-3.5 hover:bg-red-900/30 text-red-400 rounded-xl transition-all text-sm font-medium"
          >
            <LogOut size={20} />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* ── CONTENU PRINCIPAL ── */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">

        {/* TOPBAR (mobile + desktop) */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-100 shadow-sm">
          <div className="flex items-center justify-between px-4 md:px-8 h-14 md:h-16">
            {/* Hamburger mobile */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-slate-600 hover:bg-slate-50 rounded-xl transition-colors"
            >
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>

            {/* Titre page */}
            <div className="flex items-center gap-2">
              <h1 className="text-lg md:text-2xl font-black text-slate-900 uppercase tracking-tight">
                {tabLabel}
              </h1>
            </div>

            {activeTab === 'salons' ? (
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 md:px-5 md:py-2.5 rounded-2xl text-xs md:text-sm font-bold shadow-xl shadow-blue-200 transition-all hover:bg-blue-700 active:scale-95"
              >
                <Plus size={16} />
                <span className="hidden sm:inline">Ajouter un Salon</span>
                <span className="sm:hidden">Ajouter</span>
              </button>
            ) : (
              <div className="w-10" />
            )}
          </div>
        </header>

        {/* BODY */}
        <main className="flex-1 p-4 md:p-8 lg:p-10">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <Loader2 className="animate-spin text-blue-600" size={40} />
            </div>
          ) : (
            <>
              {/* ── VUE D'ENSEMBLE ── */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* KPI Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                    {caVisible && (
                      <KpiCard
                        icon={<Euro size={26} />}
                        color="emerald"
                        label="Chiffre d'Affaires Global"
                        value={`${(stats?.chiffreAffairePlateformeEuro || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
                      />
                    )}
                    <KpiCard
                      icon={<Store size={26} />}
                      color="blue"
                      label="Salons Partenaires"
                      value={`${stats?.nombreDeSalons || 0} Actifs`}
                    />
                    <KpiCard
                      icon={<Users size={26} />}
                      color="purple"
                      label="Utilisateurs Totaux"
                      value={`${stats?.totalUsers || 0} Comptes`}
                    />
                  </div>

                  {/* Aperçu rapide des salons */}
                  <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
                    <div className="p-5 md:p-7 border-b border-slate-50 flex items-center justify-between">
                      <h2 className="font-black text-slate-800 text-lg">Derniers salons</h2>
                      <button
                        onClick={() => setActiveTab('salons')}
                        className="flex items-center gap-1 text-blue-600 font-bold text-xs hover:underline"
                      >
                        Voir tout <ChevronRight size={14} />
                      </button>
                    </div>
                    <div className="divide-y divide-slate-50">
                      {salons.slice(0, 5).map(salon => (
                        <div key={salon._id} className="flex items-center justify-between px-5 md:px-7 py-4 hover:bg-slate-50/50 transition-colors">
                          <div>
                            <p className="font-bold text-slate-800 text-sm">{salon.nom}</p>
                            <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                              <MapPin size={10} /> {salon.adresse}
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${
                            salon.isActive !== false
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-red-50 text-red-700'
                          }`}>
                            {salon.isActive !== false ? 'Actif' : 'Suspendu'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ── SALONS ── */}
              {activeTab === 'salons' && (
                <div className="space-y-4">

                  {/* DESKTOP : tableau */}
                  <div className="hidden md:block bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
                    <table className="w-full text-left">
                      <thead className="bg-slate-50/50 border-b border-slate-100">
                        <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          <th className="p-5">Salon</th>
                          <th className="p-5">Admin</th>
                          {caVisible && <th className="p-5">Chiffre d'Affaires</th>}
                          <th className="p-5">Contact</th>
                          <th className="p-5">Statut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {salons.map((salon) => (
                          <tr key={salon._id} className="hover:bg-slate-50 transition-colors">
                            <td className="p-5">
                              <div className="font-bold text-slate-900 text-sm">{salon.nom}</div>
                              <div className="text-[10px] text-slate-400 flex items-center mt-1 uppercase font-bold tracking-tighter">
                                <MapPin size={10} className="mr-1" /> {salon.adresse}
                              </div>
                            </td>
                            <td className="p-5 font-semibold text-slate-700 text-sm">
                              {salon.adminPrenom} {salon.adminNom}
                            </td>
                            {caVisible && (
                              <td className="p-5">
                                <span className="text-sm font-black text-slate-900">
                                  {(salon.chiffreAffaire || 0).toLocaleString()}
                                  <span className="ml-1 text-[10px] text-blue-500">{salon.devise}</span>
                                </span>
                                {salon.chiffreAffaire > 0 && (
                                  <div className="w-12 h-1 bg-emerald-100 rounded-full mt-1 overflow-hidden">
                                    <div className="bg-emerald-500 h-full w-2/3"></div>
                                  </div>
                                )}
                              </td>
                            )}
                            <td className="p-5 text-sm text-slate-600 font-medium">{salon.adminEmail}</td>
                            <td className="p-5">
                              <button
                                onClick={() => handleToggleSalon(salon._id)}
                                className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase border transition-all ${
                                  salon.isActive !== false
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100'
                                    : 'bg-red-50 text-red-700 border-red-100 hover:bg-red-100'
                                }`}
                              >
                                {salon.isActive !== false ? 'Actif' : 'Suspendu'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {salons.length === 0 && (
                      <div className="p-16 text-center text-slate-400 font-bold italic">
                        Aucun salon enregistré.
                      </div>
                    )}
                  </div>

                  {/* MOBILE : cartes */}
                  <div className="md:hidden space-y-3">
                    {salons.length === 0 ? (
                      <div className="bg-white p-12 rounded-[2rem] border border-slate-100 text-center text-slate-400 font-bold italic">
                        Aucun salon enregistré.
                      </div>
                    ) : (
                      salons.map((salon) => (
                        <div key={salon._id} className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm space-y-4">
                          {/* Header carte */}
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <h3 className="font-black text-slate-900">{salon.nom}</h3>
                              <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                                <MapPin size={11} /> {salon.adresse}
                              </p>
                            </div>
                            <button
                              onClick={() => handleToggleSalon(salon._id)}
                              className={`shrink-0 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase border transition-all ${
                                salon.isActive !== false
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                  : 'bg-red-50 text-red-700 border-red-100'
                              }`}
                            >
                              {salon.isActive !== false ? 'Actif' : 'Suspendu'}
                            </button>
                          </div>

                          {/* Infos */}
                          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-50">
                            <div>
                              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Admin</p>
                              <p className="text-sm font-bold text-slate-700">{salon.adminPrenom} {salon.adminNom}</p>
                            </div>
                            {caVisible && (
                              <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">CA Total</p>
                                <p className="text-sm font-black text-slate-900">
                                  {(salon.chiffreAffaire || 0).toLocaleString()}
                                  <span className="text-[10px] text-blue-500 ml-1">{salon.devise}</span>
                                </p>
                              </div>
                            )}
                            <div className="col-span-2">
                              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Email</p>
                              <p className="text-xs font-medium text-slate-600 truncate">{salon.adminEmail}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* ── UTILISATEURS ── */}
              {activeTab === 'users' && <UsersManager />}

              {/* ── INSCRIPTIONS EN ATTENTE ── */}
              {activeTab === 'inscriptions' && (
                <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-black text-slate-900">Inscriptions</h2>
                      <p className="text-slate-500 text-sm mt-1">Toutes les demandes en cours (email non vérifié ou en attente de validation)</p>
                    </div>
                    <button onClick={fetchPendingRegistrations} className="text-sm text-slate-500 hover:text-slate-700 font-semibold flex items-center gap-1">
                      <Loader2 size={14} className={pendingLoading ? 'animate-spin' : ''} /> Actualiser
                    </button>
                  </div>

                  {pendingLoading ? (
                    <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-teal-600" /></div>
                  ) : pendingRegs.length === 0 ? (
                    <div className="text-center py-20 text-slate-400">
                      <ClipboardList size={48} className="mx-auto mb-4 opacity-30" />
                      <p className="font-semibold">Aucune demande en cours</p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-4">
                      {pendingRegs.map(reg => {
                        const emailNonVerifie = reg.statut === 'en_attente_email';
                        return (
                          <div key={reg._id} className={`bg-white rounded-2xl border p-5 shadow-sm ${emailNonVerifie ? 'border-amber-200' : 'border-slate-200'}`}>
                            {emailNonVerifie && (
                              <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-xl bg-amber-50 border border-amber-100">
                                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
                                <p className="text-xs font-bold text-amber-700">Email non vérifié. Le client n'a pas cliqué le lien de confirmation (expiré ou non reçu).</p>
                              </div>
                            )}
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <span className="text-base font-black text-slate-900">{reg.nomSalon}</span>
                                  <span className={`text-[11px] font-black px-2 py-0.5 rounded-full ${reg.pays === 'Sénégal' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                    {reg.pays === 'Sénégal' ? '🇸🇳' : '🇫🇷'} {reg.pays}
                                  </span>
                                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">
                                    {reg.pays === 'Sénégal' ? '25 000 FCFA/mois' : '40 €/mois'}
                                  </span>
                                  <span className={`text-[11px] font-black px-2 py-0.5 rounded-full ${emailNonVerifie ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}`}>
                                    {emailNonVerifie ? 'Email non vérifié' : 'Prêt à valider'}
                                  </span>
                                </div>
                                <p className="text-sm text-slate-600 font-medium">{reg.prenomAdmin} {reg.nomAdmin}</p>
                                <p className="text-sm text-slate-500">{reg.emailAdmin} {reg.telephone ? `. ${reg.telephone}` : ''}</p>
                                <p className="text-xs text-slate-400 mt-1">{reg.adresse}</p>
                                <p className="text-xs text-slate-400">
                                  Reçu le {new Date(reg.dateCreation).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                              <div className="flex gap-2 flex-shrink-0 flex-wrap">
                                {emailNonVerifie && (
                                  <button
                                    onClick={() => handleResend(reg._id)}
                                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-50 text-amber-700 border border-amber-200 text-sm font-bold hover:bg-amber-100 transition-colors"
                                  >
                                    Renvoyer l'email
                                  </button>
                                )}
                                <button
                                  onClick={() => handleApprove(reg._id)}
                                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-teal-600 text-white text-sm font-bold hover:bg-teal-700 transition-colors"
                                >
                                  <CheckCircle size={16} /> Valider
                                </button>
                                <button
                                  onClick={() => handleReject(reg._id)}
                                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-50 text-red-600 border border-red-200 text-sm font-bold hover:bg-red-100 transition-colors"
                                >
                                  <XCircle size={16} /> Rejeter
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* ── MODAL PIN CA ── */}
      {showPinModal && (
        <div
          className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onClick={(e) => { if (e.target === e.currentTarget) { setShowPinModal(false); setPinInput(''); setPinError(false); } }}
        >
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-xs p-8 flex flex-col items-center gap-5">
            <div className="w-14 h-14 bg-slate-900 rounded-2xl flex items-center justify-center">
              <Lock size={24} className="text-white" />
            </div>
            <div className="text-center">
              <h3 className="font-black text-slate-900 text-lg">Code confidentiel</h3>
              <p className="text-xs text-slate-400 font-medium mt-1">Entrez votre code à 4 chiffres</p>
            </div>

            <input
              ref={pinRef}
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={pinInput}
              onChange={e => { setPinError(false); setPinInput(e.target.value.replace(/\D/g, '').slice(0, 4)); }}
              onKeyDown={e => { if (e.key === 'Enter' && pinInput.length === 4) submitPin(); }}
              className={`w-full text-center text-2xl font-black tracking-[0.5em] px-4 py-3 bg-slate-50 border rounded-2xl outline-none focus:ring-2 transition-all ${
                pinError ? 'border-red-300 ring-red-200 text-red-600' : 'border-slate-200 ring-blue-500/20 text-slate-900'
              }`}
              placeholder="••••"
            />

            {pinError && (
              <p className="text-xs font-bold text-red-500 -mt-2">
                Code incorrect{pinAttempts >= 2 ? ` - plus qu'1 essai` : ''}
              </p>
            )}

            <button
              onClick={submitPin}
              disabled={pinInput.length !== 4}
              className="w-full py-3 bg-slate-900 text-white font-black rounded-2xl hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              Confirmer
            </button>
          </div>
        </div>
      )}

      {/* ── MODAL CRÉATION SALON ── */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-t-[2.5rem] sm:rounded-[2rem] w-full max-w-2xl shadow-2xl max-h-[92vh] flex flex-col">

            {/* Header */}
            <div className="p-6 md:p-8 pb-0">
              <button onClick={() => setShowModal(false)}
                className="absolute right-5 top-5 text-slate-300 hover:text-slate-600 p-1">
                <X size={22} />
              </button>
              <h2 className="text-xl font-black text-slate-900">Nouveau Partenaire</h2>
              <p className="text-slate-500 font-medium text-xs uppercase tracking-widest mt-1">
                Enregistrez un salon et créez son compte administrateur.
              </p>
            </div>

            {/* Body scrollable */}
            <div className="flex-1 overflow-y-auto px-6 md:px-8 py-4">
              <form id="createSalonForm" onSubmit={handleCreateSalon} className="space-y-5">

                {/* Infos salon */}
                <div className="space-y-4">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Informations du salon</p>

                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Nom du Salon *</label>
                    <input required
                      className="w-full mt-1 p-3.5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-medium text-sm"
                      onChange={e => setFormData({ ...formData, nomSalon: e.target.value })} />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Ville / Adresse *</label>
                      <input required
                        className="w-full mt-1 p-3.5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-medium text-sm"
                        onChange={e => setFormData({ ...formData, adresse: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Devise *</label>
                      <select
                        className="w-full mt-1 p-3.5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-blue-600 text-sm cursor-pointer"
                        value={formData.devise}
                        onChange={e => setFormData({ ...formData, devise: e.target.value })}>
                        <option value="XOF">XOF (FCFA) - Afrique</option>
                        <option value="USD">USD ($) - USA</option>
                        <option value="EUR">EUR (€) - Europe</option>
                      </select>
                    </div>
                  </div>
                </div>

                <hr className="border-slate-100" />

                {/* Infos admin */}
                <div className="space-y-4">
                  <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Identifiants Administrateur</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Prénom *</label>
                      <input required
                        className="w-full mt-1 p-3.5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-medium text-sm"
                        onChange={e => setFormData({ ...formData, prenomAdmin: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Nom *</label>
                      <input required
                        className="w-full mt-1 p-3.5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-medium text-sm"
                        onChange={e => setFormData({ ...formData, nomAdmin: e.target.value })} />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Email professionnel *</label>
                    <input type="email" required
                      className="w-full mt-1 p-3.5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-medium text-sm"
                      onChange={e => setFormData({ ...formData, emailAdmin: e.target.value })} />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Mot de passe *</label>
                      <input type="password" required
                        className="w-full mt-1 p-3.5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-medium text-sm"
                        onChange={e => setFormData({ ...formData, passwordAdmin: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Téléphone *</label>
                      <input required
                        className="w-full mt-1 p-3.5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-medium text-sm"
                        onChange={e => setFormData({ ...formData, telephoneAdmin: filterPhone(e.target.value) })} />
                    </div>
                  </div>
                </div>

              </form>
            </div>

            {/* Footer fixe */}
            <div className="p-6 md:p-8 pt-4 border-t border-slate-100 flex gap-3">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="flex-1 py-4 text-slate-400 font-bold bg-slate-50 rounded-2xl hover:bg-slate-100 transition-all text-sm"
              >
                Annuler
              </button>
              <button
                type="submit"
                form="createSalonForm"
                disabled={submitting}
                className="flex-1 py-4 bg-blue-600 text-white rounded-2xl font-bold shadow-xl shadow-blue-200 disabled:opacity-50 flex items-center justify-center gap-2 hover:bg-blue-700 transition-all text-sm"
              >
                {submitting ? <Loader2 className="animate-spin" size={20} /> : "Créer le Salon"}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

// ── COMPOSANTS INTERNES ──

const KpiCard = ({ icon, color, label, value }) => {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600'
  };
  return (
    <div className="bg-white p-6 md:p-8 rounded-[2rem] shadow-sm border border-slate-100">
      <div className={`w-13 h-13 w-14 h-14 ${colors[color]} rounded-2xl flex items-center justify-center mb-5`}>
        {icon}
      </div>
      <p className="text-slate-400 font-bold text-xs uppercase tracking-widest">{label}</p>
      <h3 className="text-2xl md:text-4xl font-black text-slate-900 mt-2 leading-none">{value}</h3>
    </div>
  );
};

export default SuperAdminDashboard;